import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { NodeEdgeMapper, Property } from '@iotv/datamodel-core';
import { isGeoGrid } from '@iotv/iotv-v3-types';
import { Alert, Grid, Skeleton, Stack } from '@mui/material';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { getNodeEdgeMapperReducer } from '../../../ContextProviders/NodeEdgeMapperComponentContext';
import { GoogleMapAPIMapTypeIds } from '../../../GoogleMapV4';
import { AreaUpdateableLabelFn } from '../../../GoogleMapV4/components/Labels/AreaUpdateablePortalLabel';
import { DefaultPortalLabel } from '../../../GoogleMapV4/components/Labels/DefaultPortalLabel';
import { NullLabel } from '../../../GoogleMapV4/components/Labels/NullLabel';
import { useVenturePlaceStyleFn } from '../../../GoogleMapV4/hooks/useVenturePlaceStyleFn';
import { useThemedMap } from '../../../GoogleMapV4/useThemedMap';
import { ObjectDetailCard } from '../../../v2/components/generic/ObjectView/ObjectDetailCard';
import { getViewDefintionFromObject } from '../../factories/AttributeDefinitions';
import { propertyToAreaPlaceEntityBranchingQuery } from './PropertyView';
const debug = process.env.REACT_APP_DEBUG && false;
/** shimmed for NetworkCoverage */
const areaData = [];
const lineDefiningPairs = [];
/** Filter the default property view definition to ignore undesireable keys (that we still want in userAttributes) */
// const defaultPropertyViewDefinition = getPropertyViewDefinition()
// const desiredPropertyViewDefKeys: (keyof typeof defaultPropertyViewDefinition)[] = ['name', 'description']
// const filteredPropertyViewDefinition: ViewDefinition = {}
// Object.entries(defaultPropertyViewDefinition).forEach(([key, viewDefEntry]) => {
//   if (desiredPropertyViewDefKeys.includes(key)) {
//     filteredPropertyViewDefinition[key] = viewDefEntry
//   }
// })
const nemReducer = getNodeEdgeMapperReducer({ canaryString: 'propertyViewMapReducer' });
const viewDef = getViewDefintionFromObject(new Property({}));
export function PropertyDetailView(props) {
    const { contextObject, transactionFunctions, userFunctions, searchParams, allowedActions, isSmallView, applicationData } = props;
    const [{ nodeEdgeMapper: mapNem, nemHash }, mapDispatch] = useReducer(nemReducer, {
        nodeEdgeMapper: new NodeEdgeMapper(),
        nemHash: null,
        apexNode: null,
    });
    /** Load new areas when app data updates */
    useEffect(() => {
        if (!applicationData)
            return;
        //mapDispatch({ type: 'clearNEMContents', value: null }) // I think clearing it is necessary because we might have loaded a new context object, not sure tho
        const mapRelevantNodes = applicationData.applyBranchingQuery([contextObject], propertyToAreaPlaceEntityBranchingQuery).getContainedNodesAndEdges();
        mapRelevantNodes.push(contextObject);
        debug && console.log('PropertyView: map relevant nodes', mapRelevantNodes);
        mapDispatch({ type: 'loadItems', value: { items: mapRelevantNodes ?? [] } });
    }, [applicationData, contextObject]);
    /** Map */
    const mapTypeId = 'terrain';
    const dataFnRef = useRef((async (gob) => {
        return { ...gob, data: {} };
    }));
    const AreaLabelFnRef = useRef(({ vob }) => vob.metadata?.generatedFrom ? (_jsx(NullLabel, { vob })) : isGeoGrid(vob) ? (_jsx(_Fragment, {})) : (_jsx(AreaUpdateableLabelFn, { vob, contextArea: props.contextObject })));
    const PlaceLabelFnRef = useRef(({ vob }) => _jsx(DefaultPortalLabel, { vob }));
    const { placeStyleFn } = useVenturePlaceStyleFn();
    const getUseThemedMapInput = useCallback(() => {
        debug && console.log(`getUseThemedMapInput useThemedMapInput callback`, nemHash);
        return {
            canaryString: 'PropertyDetailView',
            contextObject,
            nodeEdgeMapper: mapNem,
            areaData,
            lineDefiningPairs,
            nodeEdgeMapperDispatch: mapDispatch,
            transactionFunctions,
            options: {
                mapTypeId,
                mapTypeControlOptions: {
                    position: google.maps.ControlPosition.BOTTOM_RIGHT,
                    mapTypeIds: Object.keys(GoogleMapAPIMapTypeIds),
                },
                controlSize: 24,
                tilt: 0,
            },
            dataFn: dataFnRef.current,
            functions: {},
            showDrawingTools: true,
            themesAndLabels: {
                Area: {
                    LabelComponent: AreaLabelFnRef.current,
                },
                Place: {
                    LabelComponent: PlaceLabelFnRef.current,
                    styleFn: placeStyleFn,
                },
            },
        };
    }, [mapNem, contextObject, nemHash, placeStyleFn]);
    const { map, components: { PortalDiv, MapDatalayerVisibility, MapLegend }, quarantinedItemsDialog, mapActionsDialog, state: { center, bounds, map: mapObject }, } = useThemedMap(getUseThemedMapInput());
    const direction = isSmallView ? 'column' : 'row';
    const controlsMargin = 12;
    const controlsWidth = isSmallView ? '250px' : `calc(100% - ${2 * controlsMargin}px)`;
    const justifyContent = isSmallView ? undefined : 'flex-end';
    const spacing = isSmallView ? 0 : 1;
    const controlsStack = (_jsxs(Stack, { width: controlsWidth, spacing, direction, sx: { zIndex: 2, height: 0, position: 'relative', top: controlsMargin, left: controlsMargin }, children: [_jsxs(Stack, { direction, spacing, width: isSmallView ? undefined : '75%', children: [_jsx(Stack, { id: 'visibility', children: MapDatalayerVisibility }, 'nwcmMapDatalayerVisibility'), _jsx(Stack, { id: 'mapControlModalBase' }, 'EC')] }, 'left'), _jsx(Stack, { direction, width: isSmallView ? '100%' : '25%', justifyContent, children: _jsx(Stack, { id: 'legend', children: MapLegend }, 'nwcmLeg') }, 'right')] }, 'controls'));
    return (_jsxs(Stack, { gap: 1, children: [contextObject && (_jsx(ObjectDetailCard, { mostProximateCustomer: undefined,
                matchedPrimary: contextObject,
                //editMode: ComponentMode.EDIT,
                transactionFunctions,
                userFunctions,
                searchParams,
                viewDefinition: viewDef,
                //deletable: true,
                allowedActions })), contextObject && (contextObject.points?.length ?? 0) === 0 && _jsxs(Alert, { severity: "info", children: [contextObject.name, " does not have a map area"] }), _jsx(Grid, { container: true, children: _jsxs(Grid, { size: 12, sx: { minHeight: isSmallView ? '40vh' : '72cqh' }, children: [PortalDiv, controlsStack, map.current, !map.current && _jsx(Skeleton, { animation: 'wave', variant: 'rectangular', height: '100%' })] }) }), mapActionsDialog] }));
}
